import React, { useEffect, useState } from "react";
import { Image, Platform, Text, View } from "react-native";
import tw from "twrnc";

import { headingAnchor } from "./helpTopics";
import {
  parseBlocks,
  parseInline,
  previewBlocks,
  splitHelpMarkdown,
} from "./markdownParse";

export { parseBlocks, parseInline, previewBlocks, splitHelpMarkdown };
import { colors } from "./theme";

// A small Markdown renderer for Help Center articles (eventlive.pro/help/<slug>.md):
// headings, paragraphs, lists, images, rules, quotes, code and inline bold,
// italic, code and links. Enough for the help articles; not a general parser.

const Inline = ({ text, style, onLinkPress }) => (
  <Text style={style}>
    {parseInline(text).map((part, i) => {
      if (part.href) {
        return (
          <Text
            key={i}
            accessibilityRole="link"
            onPress={() => onLinkPress?.(part.href)}
            style={{ color: colors.brand, textDecorationLine: "underline" }}
          >
            {part.text}
          </Text>
        );
      }
      return (
        <Text
          key={i}
          style={[
            part.bold && { fontWeight: "700" },
            part.italic && { fontStyle: "italic" },
            part.code && {
              fontFamily: MONO,
              backgroundColor: colors.surfaceStrong,
            },
          ]}
        >
          {part.text}
        </Text>
      );
    })}
  </Text>
);

const MarkdownImage = ({ src, alt }) => {
  const [ratio, setRatio] = useState(16 / 10);
  useEffect(() => {
    let active = true;
    Image.getSize(
      src,
      (w, h) => active && w > 0 && h > 0 && setRatio(w / h),
      () => {}
    );
    return () => {
      active = false;
    };
  }, [src]);
  return (
    <Image
      source={{ uri: src }}
      accessibilityLabel={alt}
      resizeMode="contain"
      style={[
        tw`w-full my-3 rounded-lg`,
        { aspectRatio: ratio, maxHeight: 420, backgroundColor: colors.surface },
      ]}
    />
  );
};

// iOS has no "monospace" family (an unknown family is a red-screen error there).
const MONO = Platform.select({ ios: "Menlo", default: "monospace" });

const BODY = tw`text-[15px] leading-[22px] text-[${colors.ink}]`;
const HEADINGS = {
  1: tw`text-[22px] leading-[28px] font-bold text-[${colors.ink}] mt-6 mb-2`,
  2: tw`text-[18px] leading-[24px] font-bold text-[${colors.ink}] mt-6 mb-2`,
  3: tw`text-[16px] leading-[22px] font-bold text-[${colors.ink}] mt-5 mb-1.5`,
  4: tw`text-[15px] leading-[22px] font-bold text-[${colors.ink}] mt-4 mb-1`,
};

// onHeadingLayout(anchor, y) reports each heading's position so the caller can
// scroll to a section. Pass `blocks` (from previewBlocks) instead of
// `markdown` to render part of an article; `compact` keeps headings small.
const Markdown = ({
  markdown,
  blocks: givenBlocks,
  onLinkPress,
  onHeadingLayout,
  highlight,
  compact,
}) => {
  const blocks = givenBlocks || parseBlocks(markdown);
  return (
    <View>
      {blocks.map((block, i) => {
        switch (block.type) {
          case "h": {
            const anchor = headingAnchor(block.text);
            const isTarget = highlight && anchor === headingAnchor(highlight);
            return (
              <View
                key={i}
                onLayout={(e) =>
                  onHeadingLayout?.(anchor, e.nativeEvent.layout.y)
                }
                style={
                  isTarget && [
                    tw`-mx-2 px-2 py-1.5 mt-4 mb-2 rounded-md`,
                    i === 0 && tw`mt-0`,
                    { backgroundColor: colors.brandTint },
                  ]
                }
              >
                <Inline
                  text={block.text}
                  onLinkPress={onLinkPress}
                  style={[
                    HEADINGS[
                      compact ? Math.max(block.level, 3) : block.level
                    ] || HEADINGS[4],
                    (i === 0 || isTarget) && tw`mt-0`,
                    isTarget && tw`mb-0`,
                  ]}
                />
              </View>
            );
          }
          case "p":
            return (
              <Inline
                key={i}
                text={block.text}
                onLinkPress={onLinkPress}
                style={[BODY, tw`mb-3`]}
              />
            );
          case "quote":
            return (
              <View
                key={i}
                style={[
                  tw`pl-3 mb-3`,
                  { borderLeftWidth: 3, borderLeftColor: colors.divider },
                ]}
              >
                <Inline
                  text={block.text}
                  onLinkPress={onLinkPress}
                  style={[BODY, { color: colors.muted }]}
                />
              </View>
            );
          case "list":
            return (
              <View key={i} style={tw`mb-3`}>
                {block.items.map((item, j) => (
                  <View
                    key={j}
                    style={[tw`flex-row mb-1.5`, item.nested && tw`ml-5`]}
                  >
                    <Text style={[BODY, tw`w-6`]}>
                      {block.ordered ? `${item.number || j + 1}.` : "•"}
                    </Text>
                    <Inline
                      text={item.text}
                      onLinkPress={onLinkPress}
                      style={[BODY, tw`flex-1`]}
                    />
                  </View>
                ))}
              </View>
            );
          case "img":
            return <MarkdownImage key={i} src={block.src} alt={block.alt} />;
          case "hr":
            return (
              <View
                key={i}
                style={[
                  tw`my-4`,
                  { height: 1, backgroundColor: colors.divider },
                ]}
              />
            );
          case "code":
            return (
              <Text
                key={i}
                style={[
                  tw`p-3 mb-3 rounded-lg text-[13px]`,
                  {
                    fontFamily: MONO,
                    backgroundColor: colors.surfaceStrong,
                    color: colors.ink,
                  },
                ]}
              >
                {block.text}
              </Text>
            );
          default:
            return null;
        }
      })}
    </View>
  );
};

export default Markdown;
